import { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";
import Header from "@/components/Header";
import Seo from "@/components/Seo";
import ContactBar from "@/components/ContactBar";
import ContactForm from "@/components/ContactForm";
import FAQSection from "@/components/FAQSection";
import Footer from "@/components/Footer";
import BookCallButton from "@/components/BookCall";
import ServiceIcon from "@/components/ServiceIcons";
import Reveal from "@/components/Reveal";
import { sz, txt, useContent } from "@/content/ContentContext";
import { buildServiceFaqs, resolveFaqs } from "@/content/faqData";
import { breadcrumbSchema, serviceMeta, serviceSchema } from "@/content/seoData";

const ServiceDetail = () => {
  const { slug } = useParams();
  const c = useContent();
  const o = c.services.overview;
  const umbrellas = c.services.umbrellas || [];

  const flat = useMemo(
    () =>
      umbrellas.flatMap((umbrella) =>
        (umbrella.services || []).map((service) => ({ umbrella, service }))
      ),
    [umbrellas]
  );

  const current = useMemo(
    () => flat.find((entry) => entry.service.slug === slug),
    [flat, slug]
  );

  if (!current) {
    return <Navigate to="/services" replace />;
  }

  const { umbrella, service } = current;
  const related = (service.related || [])
    .map((relatedSlug) =>
      flat.find((entry) => entry.service.slug === relatedSlug)
    )
    .filter(Boolean);

  return (
    <div className="App">
      <Seo
        title={serviceMeta(service).title}
        description={serviceMeta(service).description}
        path={`/services/${service.slug}`}
        schemas={[
          serviceSchema(service, umbrella),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: serviceMeta(service).title, path: `/services/${service.slug}` },
          ]),
        ]}
      />
      <Header />
      <main>
        <section className="svcpage-hero">
          <Reveal className="svcpage-hero-inner" y={18}>
            <nav className="svcd-breadcrumb" aria-label="Breadcrumb">
              <Link to="/services" data-testid="breadcrumb-services">
                Services
              </Link>
              <ChevronRight className="svcd-crumb-sep" aria-hidden="true" />
              <Link
                to={`/services#${umbrella.slug}`}
                data-testid="breadcrumb-umbrella"
              >
                {txt(umbrella.title)}
              </Link>
              <ChevronRight className="svcd-crumb-sep" aria-hidden="true" />
              <span aria-current="page">{txt(service.title)}</span>
            </nav>
            <span className="svcd-hero-icon">
              <ServiceIcon name={service.icon} />
            </span>
            <h1
              className="svcpage-title"
              style={sz(service.title)}
              data-testid="page-title"
            >
              {txt(service.title)}
            </h1>
            <p className="svcd-hook" style={sz(service.hook)}>
              {txt(service.hook)}
            </p>
            <BookCallButton
              className="btn-outline-light"
              testId="service-hero-cta"
            />
            <a
              href="#message"
              className="alt-contact-link on-dark"
              style={sz(c.global.altContactLabel)}
              data-testid="service-hero-alt-contact"
            >
              {txt(c.global.altContactLabel)}
            </a>
          </Reveal>
        </section>

        <section className="svcd-body">
          <Reveal className="svcd-intro" y={20}>
            {(service.paragraphs || []).map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </Reveal>

          <div className="svcd-panels">
            <Reveal className="svcd-panel svcd-panel-help" y={24}>
              <h2 className="svcd-panel-title" style={sz(o.howWeHelpLabel)}>
                {txt(o.howWeHelpLabel)}
              </h2>
              <ul className="svcd-help-list" data-testid="how-we-help-list">
                {(service.howWeHelp || []).map((item, index) => (
                  <li key={index}>
                    <span className="svcd-help-dot" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal className="svcd-panel svcd-panel-ideal" y={24} delay={0.1}>
              <h2 className="svcd-panel-title" style={sz(o.idealForLabel)}>
                {txt(o.idealForLabel)}
              </h2>
              <ul className="svcd-ideal-list" data-testid="ideal-for-list">
                {(service.idealFor || []).map((item, index) => (
                  <li key={index}>
                    <Check className="svcd-check" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          {related.length > 0 && (
            <Reveal className="svcd-related" y={22}>
              <h2 className="svcd-related-title" style={sz(o.relatedLabel)}>
                {txt(o.relatedLabel)}
              </h2>
              <div className="svcd-related-grid">
                {related.map((entry) => (
                  <Link
                    key={entry.service.slug}
                    to={`/services/${entry.service.slug}`}
                    className="svcd-related-card"
                    data-testid={`related-${entry.service.slug}`}
                  >
                    <span className="svcd-related-icon">
                      <ServiceIcon name={entry.service.icon} />
                    </span>
                    <span className="svcd-related-main">
                      <span className="svcd-related-name">
                        {txt(entry.service.title)}
                      </span>
                      <span className="svcd-related-umbrella">
                        {txt(entry.umbrella.title)}
                      </span>
                    </span>
                    <ChevronRight
                      className="svcd-related-arrow"
                      aria-hidden="true"
                    />
                  </Link>
                ))}
              </div>
            </Reveal>
          )}
        </section>

        <Reveal as="section" className="cta-banner" y={20}>
          <div className="cta-banner-text">
            <h3 style={sz(o.banner.heading)}>{txt(o.banner.heading)}</h3>
            <p style={sz(o.banner.text)}>{txt(o.banner.text)}</p>
          </div>
          <div className="cta-banner-actions">
            <BookCallButton
              className="btn-outline-light"
              testId="service-banner-cta"
            />
            <a
              href="#message"
              className="alt-contact-link on-dark"
              style={sz(c.global.altContactLabel)}
              data-testid="service-banner-alt-contact"
            >
              {txt(c.global.altContactLabel)}
            </a>
          </div>
        </Reveal>
      </main>
      <FAQSection items={resolveFaqs(c, `service:${service.slug}`, buildServiceFaqs(umbrella, service, c))} />
      <ContactForm />
      <ContactBar />
      <Footer />
    </div>
  );
};

export default ServiceDetail;
